import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageBanner from '@/components/site/PageBanner';
import ToolNav from '@/components/site/ToolNav';
import SEO from '@/components/site/SEO';
import { Info, Link, Check, Send, Copy, ShieldCheck, ShieldOff, ChevronRight, Pencil, X, RotateCcw } from 'lucide-react';
import { BANK_LS_KEY, SAMPLE_BANKS, loadBanks, bankDisplayName, LOAN_PRODUCT_OPTIONS } from './LoanComparisonPage';

const CALC_LS_KEY = 'finirio_calc_charges';
const GST_RATE    = 0.18;

const LOAN_TYPES = [
  { id: 'home_loan',     label: 'Home Loan' },
  { id: 'lap',           label: 'Loan Against Property' },
  { id: 'personal_loan', label: 'Personal Loan' },
  { id: 'business_loan', label: 'Business Loan' },
];

const LOAN_TYPE_CONFIG = {
  home_loan:     { hasProperty: true,  hasNoi: true,  hasModt: true,  hasDoc: true },
  lap:           { hasProperty: true,  hasNoi: true,  hasModt: true,  hasDoc: true },
  personal_loan: { hasProperty: false, hasNoi: false, hasModt: false, hasDoc: false },
  business_loan: { hasProperty: false, hasNoi: false, hasModt: false, hasDoc: false },
};

const GENERAL_DEFAULTS = {
  home_loan:     { procFeeRate: 0.5,  procFeeMin: 5000,  procFeeMax: 25000, legalCharges: 5000, techCharges: 3500, modtStampRate: 0.3, noiCharge: 17750, docCharges: 5000, propertyInsurance: 'mandatory',     propInsYears: 2, propInsPerYear: 2500, lifeInsurance: 'not_mandatory' },
  lap:           { procFeeRate: 1.0,  procFeeMin: 10000, procFeeMax: 50000, legalCharges: 7500, techCharges: 4000, modtStampRate: 0.3, noiCharge: 17750, docCharges: 7500, propertyInsurance: 'mandatory',     propInsYears: 2, propInsPerYear: 2500, lifeInsurance: 'not_mandatory' },
  personal_loan: { procFeeRate: 2.0,  procFeeMin: 1000,  procFeeMax: 15000, legalCharges: 0,    techCharges: 0,    modtStampRate: 0,   noiCharge: 0,     docCharges: 0,    propertyInsurance: 'not_mandatory', propInsYears: 0, propInsPerYear: 0,    lifeInsurance: 'not_mandatory' },
  business_loan: { procFeeRate: 2.0,  procFeeMin: 2000,  procFeeMax: 20000, legalCharges: 0,    techCharges: 0,    modtStampRate: 0,   noiCharge: 0,     docCharges: 0,    propertyInsurance: 'not_mandatory', propInsYears: 0, propInsPerYear: 0,    lifeInsurance: 'not_mandatory' },
};

const fmt    = (n) => '₹ ' + Math.round(n).toLocaleString('en-IN');
const fmtNum = (n) => Math.round(n).toLocaleString('en-IN');

const VALID_LOAN_TYPE_IDS = LOAN_TYPES.map((lt) => lt.id);

export default function ChargesCalculatorPage() {
  const location = useLocation();
  const navigate  = useNavigate();

  const [step, setStep]         = useState(1);
  const [loanType, setLoanType] = useState(() => {
    const p = new URLSearchParams(location.search);
    const t = p.get('type');
    return t && VALID_LOAN_TYPE_IDS.includes(t) ? t : 'home_loan';
  });
  const [selectedBankId, setSelectedBankId] = useState('general');
  const [loanAmount, setLoanAmount]         = useState(() => {
    const p = new URLSearchParams(location.search);
    const a = Number(p.get('amount'));
    return a > 0 ? a : 5000000;
  });
  const [banks, setBanks]               = useState([]);
  const [linkCopied, setLinkCopied]     = useState(false);
  const [chargeCopied, setChargeCopied] = useState(false);
  const [sentToComp, setSentToComp]     = useState(false);

  /* Edit state */
  const [overrides, setOverrides]       = useState({});   // { key: number }
  const [editingKey, setEditingKey]     = useState(null);
  const [editingValue, setEditingValue] = useState('');

  useEffect(() => { setBanks(loadBanks()); }, []);

  /* Sync URL */
  const syncTimer = useRef(null);
  useEffect(() => {
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      if (step < 3) return;
      const params = new URLSearchParams();
      params.set('type', loanType);
      params.set('amount', loanAmount);
      navigate({ search: params.toString() }, { replace: true });
    }, 400);
    return () => clearTimeout(syncTimer.current);
  }, [loanType, loanAmount, step, navigate]);

  /* Reset overrides when bank/loan type/amount changes */
  useEffect(() => {
    setOverrides({});
    setEditingKey(null);
  }, [selectedBankId, loanType, loanAmount]);

  /* Resolve effective bank data */
  const effectiveBank = useMemo(() => {
    if (selectedBankId === 'general') return GENERAL_DEFAULTS[loanType];
    const bank = banks.find((b) => b.id === Number(selectedBankId));
    return bank || GENERAL_DEFAULTS[loanType];
  }, [selectedBankId, loanType, banks]);

  const selectedBankName = useMemo(() => {
    if (selectedBankId === 'general') return 'General';
    const bank = banks.find((b) => b.id === Number(selectedBankId));
    return bank ? bank.name : 'General';
  }, [selectedBankId, banks]);

  /* Banks filtered to only those matching the selected loan type (or with no product = general) */
  const visibleBanks = useMemo(() => {
    const loanLabel = LOAN_TYPES.find((lt) => lt.id === loanType)?.label || '';
    return banks.filter((b) => !b.product || b.product === loanLabel);
  }, [banks, loanType]);

  /* Compute base charges (always from bank data) */
  const baseCharges = useMemo(() => {
    if (step < 3) return null;
    const cfg    = LOAN_TYPE_CONFIG[loanType];
    const amount = Number(loanAmount) || 0;
    const b      = effectiveBank;

    const base   = (amount * (b.procFeeRate || 0)) / 100;
    const min    = b.procFeeMin || 0;
    const maxCap = b.procFeeMax > 0 ? b.procFeeMax : Infinity;
    const capped = Math.min(Math.max(base, min), maxCap);
    const procFee = capped * (1 + GST_RATE);
    const modt    = cfg.hasModt ? (amount * (b.modtStampRate || 0)) / 100 : 0;
    const noi     = cfg.hasNoi  ? (b.noiCharge    || 0) : 0;
    const legal   = b.legalCharges || 0;
    const tech    = b.techCharges  || 0;
    const doc     = cfg.hasDoc  ? (b.docCharges   || 0) : 0;
    const propIns = cfg.hasProperty && b.propertyInsurance === 'mandatory'
      ? (b.propInsYears || 0) * (b.propInsPerYear || 0) : 0;

    return [
      {
        key: 'procFee', label: 'Processing Fee (incl. GST)', amount: procFee, show: true,
        note: `${b.procFeeRate}% → ${fmt(capped)} base + 18% GST${b.procFeeMax > 0 ? ` (cap ${fmt(b.procFeeMax)})` : ''}, min ${fmt(min)}`,
      },
      {
        key: 'modt', label: 'MODT / Stamp Duty', amount: modt, show: cfg.hasModt && modt > 0,
        note: `${b.modtStampRate}% of loan amount — charged by state government`,
      },
      {
        key: 'noi', label: 'NOI — Notice of Intimation', amount: noi, show: cfg.hasNoi && noi > 0,
        note: 'Registration of equitable mortgage with Sub-Registrar',
      },
      {
        key: 'legal', label: 'Legal Charges', amount: legal, show: legal > 0,
        note: 'Legal / advocate fee for property title verification',
      },
      {
        key: 'tech', label: 'Technical / Valuation Charges', amount: tech, show: tech > 0,
        note: 'Property valuation by bank-empanelled engineer',
      },
      {
        key: 'doc', label: 'Documentation Charges', amount: doc, show: cfg.hasDoc && doc > 0,
        note: 'Stamp paper, Notary, CERSAI, CIBIL — approx.',
      },
      {
        key: 'propIns',
        label: `Property Insurance${b.propInsYears > 0 ? ` (${b.propInsYears} yr${b.propInsYears > 1 ? 's' : ''})` : ''}`,
        amount: propIns,
        show: cfg.hasProperty && b.propertyInsurance === 'mandatory' && propIns > 0,
        note: `${fmt(b.propInsPerYear)} × ${b.propInsYears} yr${b.propInsYears > 1 ? 's' : ''} — Mandatory`,
        isMandatory: true,
      },
    ].filter((c) => c.show);
  }, [step, loanType, loanAmount, effectiveBank]);

  /* Effective charges = base + user overrides + recalculated total */
  const effectiveCharges = useMemo(() => {
    if (!baseCharges) return null;
    const rows = baseCharges.map((c) => ({
      ...c,
      amount:     overrides.hasOwnProperty(c.key) ? overrides[c.key] : c.amount,
      overridden: overrides.hasOwnProperty(c.key),
    }));
    const total = rows.reduce((s, c) => s + c.amount, 0);
    return [...rows, { key: 'total', label: 'Total Estimated Charges', amount: total, show: true, isTotal: true }];
  }, [baseCharges, overrides]);

  /* Insurance flags from effective data */
  const propInsInfo = useMemo(() => {
    const cfg = LOAN_TYPE_CONFIG[loanType];
    const b   = effectiveBank;
    if (!cfg.hasProperty) return null;
    const isOverriddenToZero = overrides.hasOwnProperty('propIns') && overrides['propIns'] === 0;
    const mandatory = !isOverriddenToZero && b.propertyInsurance === 'mandatory';
    return { mandatory, years: b.propInsYears, perYear: b.propInsPerYear };
  }, [loanType, effectiveBank, overrides]);

  const lifeInsInfo = useMemo(() => {
    const cfg = LOAN_TYPE_CONFIG[loanType];
    if (!cfg.hasProperty) return null;
    return { mandatory: effectiveBank.lifeInsurance === 'mandatory' };
  }, [loanType, effectiveBank]);

  /* Edit handlers */
  function startEdit(charge) {
    setEditingKey(charge.key);
    setEditingValue(String(Math.round(charge.amount)));
  }
  function confirmEdit() {
    const val = Math.max(0, Number(editingValue) || 0);
    setOverrides((prev) => ({ ...prev, [editingKey]: val }));
    setEditingKey(null);
  }
  function cancelEdit() { setEditingKey(null); }
  function resetOverride(key) {
    setOverrides((prev) => { const n = { ...prev }; delete n[key]; return n; });
  }

  /* Copy charges as text (effectiveCharges, no note) */
  function handleCopyCharges() {
    if (!effectiveCharges) return;
    const loanLabel = LOAN_TYPES.find((lt) => lt.id === loanType)?.label || loanType;
    const rows      = effectiveCharges.filter((c) => !c.isTotal);
    const lines = [
      `*${selectedBankName} — ${loanLabel} — ₹${fmtNum(loanAmount)}*`,
      '─'.repeat(42),
      ...rows.map((c) => `${c.label}: ₹${fmtNum(c.amount)}`),
      `Property Insurance: ${propInsInfo?.mandatory ? `Mandatory (${propInsInfo.years} yr × ₹${fmtNum(propInsInfo.perYear)}/yr)` : 'Not Required'}`,
      `Life Insurance: ${lifeInsInfo?.mandatory ? 'Mandatory' : 'Not Required'}`,
      '─'.repeat(42),
      `Total One-time: ₹${fmtNum(effectiveCharges.find((c) => c.isTotal)?.amount || 0)}`,
    ];
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setChargeCopied(true);
      setTimeout(() => setChargeCopied(false), 2500);
    });
  }

  /* Send to Loan Comparison (effectiveCharges) */
  function handleSendToComparison() {
    const get = (key) => effectiveCharges?.find((c) => c.key === key)?.amount || 0;
    const payload = { procFee: get('procFee'), modtStamp: get('modt'), noi: get('noi'), doc: get('doc') };
    localStorage.setItem(CALC_LS_KEY, JSON.stringify(payload));
    setSentToComp(true);
    setTimeout(() => { setSentToComp(false); navigate('/loan-comparison'); }, 800);
  }

  function handleShareLink() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }

  const hasAnyOverride = Object.keys(overrides).length > 0;
  const STEPS = ['Loan Type', 'Select Bank', 'Loan Amount'];

  /* ── RENDER ── */
  return (
    <main>
      <SEO
        title="Loan Charges Calculator | Bank-wise Processing Fee | Finirio"
        description="Calculate processing fees, MODT, NOI, legal and insurance charges for your home loan with bank-specific rates."
        path="/charges-calculator"
        keywords="loan charges calculator, home loan processing fee, stamp duty calculator, bank charges india"
      />
      <PageBanner title="Charges Calculator" crumbs={['Calci and Docs', 'Charges Calculator']} />
      <ToolNav />

      <section className="py-14 bg-white">
        <div className="max-w-[860px] mx-auto px-4 lg:px-6">

          <div className="mb-8">
            <h2 className="font-heading text-xl font-bold text-[#0b2545]">Estimate Loan Charges</h2>
            <p className="text-slate-500 text-sm mt-1">Bank-specific processing fee with min/max cap + all upfront charges.</p>
          </div>

          {/* ── Step indicator ── */}
          <div className="flex items-center gap-2 mb-8">
            {STEPS.map((s, i) => {
              const n      = i + 1;
              const done   = step > n;
              const active = step === n;
              return (
                <React.Fragment key={s}>
                  <button
                    onClick={() => step > n && setStep(n)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[13px] font-medium border transition ${
                      done   ? 'border-[#2ea690] bg-[#f0faf8] text-[#2ea690] cursor-pointer'
                      : active ? 'border-[#1e73be] bg-[#1e73be] text-white'
                      : 'border-slate-200 bg-slate-50 text-slate-400 cursor-default'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center ${done ? 'bg-[#2ea690] text-white' : active ? 'bg-white text-[#1e73be]' : 'bg-slate-200 text-slate-400'}`}>
                      {done ? '✓' : n}
                    </span>
                    {s}
                  </button>
                  {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />}
                </React.Fragment>
              );
            })}
          </div>

          {/* ── Step 1: Loan Type ── */}
          {step === 1 && (
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-3">Select Loan Type</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {LOAN_TYPES.map((lt) => (
                  <button key={lt.id}
                    onClick={() => { setLoanType(lt.id); setStep(2); }}
                    className={`p-4 rounded-xl border-2 text-left font-medium text-[14px] transition hover:border-[#1e73be] hover:bg-[#eaf3fb] ${loanType === lt.id ? 'border-[#1e73be] bg-[#eaf3fb] text-[#1e73be]' : 'border-slate-200 text-slate-700'}`}>
                    {lt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Step 2: Bank ── */}
          {step === 2 && (
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[12px] text-[#2ea690] font-medium">Loan Type: {LOAN_TYPES.find(lt => lt.id === loanType)?.label}</span>
                <button onClick={() => setStep(1)} className="text-[11px] text-slate-400 underline hover:text-slate-600">Change</button>
              </div>
              <label className="block text-slate-700 text-sm font-medium mb-3">Select Bank</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={() => { setSelectedBankId('general'); setStep(3); }}
                  className={`p-4 rounded-xl border-2 text-left transition hover:border-[#1e73be] hover:bg-[#eaf3fb] ${selectedBankId === 'general' ? 'border-[#1e73be] bg-[#eaf3fb]' : 'border-slate-200'}`}>
                  <p className="font-semibold text-[14px] text-slate-800">General / No Specific Bank</p>
                  <p className="text-[12px] text-slate-400 mt-0.5">Use standard default rates</p>
                </button>
                {visibleBanks.map((bank) => (
                  <button key={bank.id} onClick={() => { setSelectedBankId(String(bank.id)); setStep(3); }}
                    className={`p-4 rounded-xl border-2 text-left transition hover:border-[#1e73be] hover:bg-[#eaf3fb] ${selectedBankId === String(bank.id) ? 'border-[#1e73be] bg-[#eaf3fb]' : 'border-slate-200'}`}>
                    <p className="font-semibold text-[14px] text-slate-800">{bank.name}</p>
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      <span className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">PF {bank.procFeeRate}% (cap {bank.procFeeMax > 0 ? `₹${(bank.procFeeMax / 1000).toFixed(0)}K` : 'none'})</span>
                      {bank.propertyInsurance === 'mandatory' && (
                        <span className="text-[11px] bg-red-50 text-red-600 px-2 py-0.5 rounded-full">Prop. Ins. Mandatory</span>
                      )}
                      {bank.lifeInsurance === 'mandatory' && (
                        <span className="text-[11px] bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full">Life Ins. Mandatory</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              {visibleBanks.length === 0 && (
                <p className="mt-3 text-[13px] text-slate-400 italic">
                  Koi specific bank rates saved nahi hain is loan type ke liye. General select karein ya Loan Comparison mein bank table mein entries add karein.
                </p>
              )}
            </div>
          )}

          {/* ── Step 3: Loan Amount + Results ── */}
          {step === 3 && (
            <>
              <div className="flex items-center gap-3 mb-1 flex-wrap">
                <span className="text-[12px] text-[#2ea690] font-medium">
                  {LOAN_TYPES.find(lt => lt.id === loanType)?.label} — {selectedBankName}
                </span>
                <button onClick={() => setStep(2)} className="text-[11px] text-slate-400 underline hover:text-slate-600">Change bank</button>
                <button onClick={() => setStep(1)} className="text-[11px] text-slate-400 underline hover:text-slate-600">Change loan type</button>
              </div>

              <div className="mb-8">
                <label className="block text-slate-700 text-sm font-medium mb-2">Loan Amount (₹)</label>
                <input
                  type="number" step={100000} min={100000}
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  className="w-full sm:max-w-xs h-11 px-3 border border-slate-300 rounded-md outline-none focus:ring-2 focus:ring-[#1e73be]/30"
                />
              </div>

              {/* Charges list */}
              {effectiveCharges && (
                <>
                  {/* Edit mode hint */}
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[12px] text-slate-400 flex items-center gap-1">
                      <Pencil className="w-3 h-3" /> Click the pencil icon on any row to edit the amount before copying.
                    </p>
                    {hasAnyOverride && (
                      <button
                        onClick={() => setOverrides({})}
                        className="text-[11px] text-red-500 underline hover:text-red-700 flex items-center gap-0.5"
                      >
                        <RotateCcw className="w-3 h-3" /> Reset all edits
                      </button>
                    )}
                  </div>

                  <div className="card-lux overflow-hidden">
                    {effectiveCharges.map((charge, i) => {
                      const isEditing = editingKey === charge.key;
                      if (charge.isTotal) {
                        return (
                          <div key="total" className="flex items-center justify-between gap-4 px-5 py-4 border-t-2 border-[#c9a24b]" style={{ backgroundColor: '#FBF9F4' }}>
                            <p className="font-heading font-bold text-[#0b2545] text-[15px]">Total Estimated Charges</p>
                            <p className="font-heading font-bold text-[18px] text-[#a9812f]">{fmt(charge.amount)}</p>
                          </div>
                        );
                      }
                      return (
                        <div key={charge.key}
                          className={`flex items-start justify-between gap-4 px-5 py-4 ${i > 0 ? 'border-t border-slate-100' : ''} ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'} group`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-700 text-[14px]">
                                {charge.label}
                                {charge.isMandatory && !charge.overridden && (
                                  <span className="ml-2 text-[10px] bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5 rounded-full">Mandatory</span>
                                )}
                                {charge.overridden && (
                                  <span className="ml-2 text-[10px] bg-amber-50 text-amber-700 border border-amber-300 px-1.5 py-0.5 rounded-full">✏ Edited</span>
                                )}
                              </p>
                            </div>
                            <p className="text-slate-400 text-[12px] mt-0.5 flex items-start gap-1">
                              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />{charge.note}
                            </p>
                          </div>

                          {/* Amount + edit controls */}
                          <div className="flex-shrink-0 flex items-center gap-2">
                            {isEditing ? (
                              /* Inline edit input */
                              <div className="flex items-center gap-1.5">
                                <span className="text-slate-500 text-[13px]">₹</span>
                                <input
                                  type="number"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onFocus={(e) => e.target.select()}
                                  onKeyDown={(e) => { if (e.key === 'Enter') confirmEdit(); if (e.key === 'Escape') cancelEdit(); }}
                                  autoFocus
                                  className="w-28 h-8 px-2 text-[13px] border border-[#1e73be] rounded-md outline-none focus:ring-2 focus:ring-[#1e73be]/30 text-right"
                                />
                                <button onClick={confirmEdit}
                                  className="w-7 h-7 rounded-full bg-[#2ea690] text-white flex items-center justify-center hover:brightness-110 transition">
                                  <Check className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={cancelEdit}
                                  className="w-7 h-7 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center hover:bg-slate-300 transition">
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className={`font-semibold text-[15px] ${charge.overridden ? 'text-amber-700' : 'text-slate-800'}`}>
                                  {fmt(charge.amount)}
                                </span>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                                  <button
                                    onClick={() => startEdit(charge)}
                                    title="Edit amount"
                                    className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-[#1e73be] hover:bg-[#eaf3fb] transition">
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                  {charge.overridden && (
                                    <button
                                      onClick={() => resetOverride(charge.key)}
                                      title="Reset to calculated value"
                                      className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition">
                                      <RotateCcw className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Property Insurance status row */}
                    {propInsInfo && (
                      <div className="flex items-start justify-between gap-4 px-5 py-4 border-t border-slate-100 bg-white group">
                        <div className="flex-1">
                          <p className="text-[14px] font-medium text-slate-700">Property Insurance</p>
                          <p className="text-[12px] text-slate-400 mt-0.5 flex items-start gap-1">
                            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            {propInsInfo.mandatory
                              ? `${propInsInfo.years} yr × ${fmt(propInsInfo.perYear)}/yr — included in total above`
                              : overrides.hasOwnProperty('propIns') && overrides['propIns'] === 0
                              ? 'Manually set to ₹0 — not included in total'
                              : 'Not required by this bank'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold border ${propInsInfo.mandatory ? 'bg-red-50 text-red-600 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                            {propInsInfo.mandatory ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
                            {propInsInfo.mandatory ? 'Mandatory' : 'Not Required'}
                          </span>
                          {/* Toggle: if mandatory, allow overriding to not required */}
                          {propInsInfo.mandatory && !overrides.hasOwnProperty('propIns') && (
                            <button
                              onClick={() => setOverrides((prev) => ({ ...prev, propIns: 0 }))}
                              title="Mark as Not Required for this case (e.g. under-construction)"
                              className="opacity-0 group-hover:opacity-100 transition text-[11px] text-slate-400 border border-slate-200 rounded px-2 py-0.5 hover:border-amber-400 hover:text-amber-700 hover:bg-amber-50">
                              Remove
                            </button>
                          )}
                          {overrides.hasOwnProperty('propIns') && overrides['propIns'] === 0 && (
                            <button
                              onClick={() => resetOverride('propIns')}
                              className="text-[11px] text-red-500 underline hover:text-red-700 flex items-center gap-0.5">
                              <RotateCcw className="w-3 h-3" /> Restore
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Life Insurance status row */}
                    {lifeInsInfo && (
                      <div className="flex items-start justify-between gap-4 px-5 py-4 border-t border-slate-100 bg-slate-50/60">
                        <div className="flex-1">
                          <p className="text-[14px] font-medium text-slate-700">Life Insurance</p>
                          <p className="text-[12px] text-slate-400 mt-0.5 flex items-start gap-1">
                            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            {lifeInsInfo.mandatory ? 'Mandatory for this bank — premium varies by age & sum assured' : 'Not mandatory for this bank'}
                          </p>
                        </div>
                        <span className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold border ${lifeInsInfo.mandatory ? 'bg-orange-50 text-orange-600 border-orange-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                          {lifeInsInfo.mandatory ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
                          {lifeInsInfo.mandatory ? 'Mandatory' : 'Not Required'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Standard disclaimer */}
                  <div className="mt-5 rounded-lg bg-amber-50 border border-amber-200 px-5 py-3 text-[13px] text-amber-800 leading-relaxed">
                    <strong>Note:</strong> Figures are indicative. Processing fees, stamp duty, and insurance premiums vary by lender, state, profile and property.
                  </div>

                  {/* ── Must Read — Employees only (NOT copied) ── */}
                  <div className="mt-3 rounded-lg bg-red-50 border border-red-300 px-5 py-3.5 text-[13px] text-red-800 leading-relaxed">
                    <strong className="flex items-center gap-1.5 text-red-700 mb-1">⚠️ Must Read — Employees:</strong>
                    <p>
                      <strong>1 Cr+ cases</strong> mein kaafi baari Legal aur Technical charges <strong>double</strong> lag sakte hain —
                      2 Legal + 2 Technical ya kaafi baari <strong>2 Legal + 1 Technical</strong>. 
                      Copy karne se pehle actual case ke hisaab se check karein aur zaroorat ho toh row edit karein.
                    </p>
                  </div>

                  {/* Action buttons */}
                  <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={handleCopyCharges}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-semibold border transition ${chargeCopied ? 'border-[#2ea690] text-[#2ea690] bg-[#f0faf8]' : 'border-[#1e73be] text-[#1e73be] bg-[#eaf3fb] hover:bg-[#dce8f7]'}`}>
                        {chargeCopied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy Charges</>}
                      </button>

                      <button onClick={handleSendToComparison}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-semibold text-white hover:brightness-110 transition shadow-sm"
                        style={{ backgroundColor: sentToComp ? '#2ea690' : '#0f3d78' }}>
                        {sentToComp ? <><Check className="w-3.5 h-3.5" /> Sent! Redirecting…</> : <><Send className="w-3.5 h-3.5" /> Send to Loan Comparison</>}
                      </button>
                    </div>

                    <button onClick={handleShareLink}
                      className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-md border text-[13px] font-medium transition"
                      style={linkCopied ? { borderColor: '#2ea690', color: '#2ea690', backgroundColor: '#f0faf8' } : { borderColor: '#cbd5e1', color: '#475569', backgroundColor: '#fff' }}>
                      {linkCopied ? <><Check className="w-3.5 h-3.5" /> Link copied!</> : <><Link className="w-3.5 h-3.5" /> Copy link</>}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

        </div>
      </section>
    </main>
  );
}
